import { categoriesSelectors } from 'app-components/flux/categories';
import { customersSelectors } from 'app-components/flux/customers';
import type { FilterObjectProps } from 'app-components/flux/filters/filtersTypes';
import { projectsSelectors } from 'app-components/flux/projects';
import type { Project } from 'app-components/flux/projects/projectsTypes';
import { mkProject } from 'app-components/flux/projects/projectsTypes';
import store from 'app-components/utils/redux-store';
import {
  CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE,
  DISPLAY_NAME_FOR_CLIENT_ONLY_UI_OPTION,
  QCS_ID_FOR_CLIENT_ONLY_UI_OPTION,
  VALUE_DELIMITER_FOR_CLIENT_ONLY_UI_OPTION,
} from 'components/QFilterSelect/constants';
import { flagFieldOptions } from 'data/transactions/config';
import { Map as ImmutableMap } from 'immutable';
import { exists } from 'utils/utils';

import ClientIcon from 'assets/icons/nav-icons/clients.inline.svg';
import AccountIcon from 'assets/icons/settings-menu/accounts.settings.simplifi.inline.svg';
import CategoryIcon from 'assets/icons/settings-menu/categories.tags.settings.simplifi.inline.svg';
import FlagIcon from 'assets/icons/settings-menu/flags.inline.svg';
import PayeeIcon from 'assets/icons/settings-menu/payee-store-front.inline.svg';
import AdvancedIcon from 'assets/icons/settings-menu/renaming.rules.settings.simplifi.inline.svg';
import TagIcon from 'assets/icons/settings-menu/tag-icon.inline.svg';

type Customer = {
  id: string;
  businessName?: string;
  firstName?: string;
  lastName?: string;
};

type CustomersAndProjectsSelectedItem = {
  customer: Customer;
  projects: Project[];
};

export const itemMenuConfig = [
  {
    resourceType: 'categories',
    title: 'Categories',
    id: 'add_filter_button_category',
    icon: CategoryIcon,
  },
  {
    resourceType: 'payeeNames',
    title: 'Payees',
    id: 'add_filter_button_payee',
    icon: PayeeIcon,
  },
  {
    resourceType: 'tags',
    title: 'Tags',
    id: 'add_filter_button_tag',
    icon: TagIcon,
  },
  {
    resourceType: 'accounts',
    title: 'Accounts',
    id: 'add_filter_button_account',
    icon: AccountIcon,
  },
  {
    resourceType: CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE,
    title: 'Clients & Projects',
    id: 'add_filter_button_clients_and_projects',
    icon: ClientIcon,
  },
  {
    resourceType: 'flags',
    title: 'Flags',
    id: 'add_filter_button_flag',
    icon: FlagIcon,
  },
  {
    resourceType: 'advanced',
    title: 'Advanced',
    id: 'add_filter_button_advanced',
    icon: AdvancedIcon,
  },
];

export const countDefinedItems = (imap?: ImmutableMap<any, any>): number => {
  if (!imap) return 0;
  return imap.reduce((v: number, x: any) => v + (exists(x) ? 1 : 0), 0);
};

export const getCustomerIdOfSelectedProject = (selectedProjectId: string): string | undefined => {
  if (!store) {
    return;
  }
  if (selectedProjectId.includes(VALUE_DELIMITER_FOR_CLIENT_ONLY_UI_OPTION)) {
    const items = selectedProjectId.split(VALUE_DELIMITER_FOR_CLIENT_ONLY_UI_OPTION);
    return items[0];
  } else {
    const projects = projectsSelectors.getProjects(store.getState());
    const projectObj = projects.get(selectedProjectId);
    return projectObj?.customerId;
  }
};

export const getCustomersAndProjectsSelectedFromSelectedIds = (
  selectedIds: string[],
): CustomersAndProjectsSelectedItem[] => {
  if (!store) {
    return [];
  }
  const customersById = customersSelectors.getCustomers(store.getState());
  const projectsByCustomerId = projectsSelectors.getProjectsByCustomerId(store.getState());

  const customerIds: string[] = [];
  const projectIds: string[] = [];
  for (const id of selectedIds) {
    if (customersById.has(id)) {
      customerIds.push(id);
    } else {
      projectIds.push(id);
    }
  }

  const customersAndProjectsSelected: CustomersAndProjectsSelectedItem[] = [];

  for (const customerId of customerIds) {
    const allProjects = projectsByCustomerId.get(customerId)?.allProjects;
    const customer = customersById.get(customerId)?.toJS() as Customer;
    if (customer && customer.id) {
      if (!allProjects) {
        customersAndProjectsSelected.push({
          customer: customer,
          projects: [new mkProject({ id: QCS_ID_FOR_CLIENT_ONLY_UI_OPTION })],
        });
      } else {
        const isAnyProjectSelected = allProjects.some((project: Project) => projectIds.includes(project.id || ''));
        if (!isAnyProjectSelected) {
          customersAndProjectsSelected.push({
            customer: customer,
            projects: [new mkProject({ id: QCS_ID_FOR_CLIENT_ONLY_UI_OPTION }), ...allProjects],
          });
        }
      }
    }
  }

  for (const projectId of projectIds) {
    const customerId = getCustomerIdOfSelectedProject(projectId);
    const customerExist = customersAndProjectsSelected.find((item) => item?.customer?.id === customerId);
    if (!customerExist) {
      const customer = customerId && (customersById.get(customerId)?.toJS() as Customer);
      customer && customersAndProjectsSelected.push({ customer, projects: [] });
    }
    const objIndex = customersAndProjectsSelected.findIndex((item) => item?.customer?.id === customerId);
    if (projectId.includes(VALUE_DELIMITER_FOR_CLIENT_ONLY_UI_OPTION)) {
      customersAndProjectsSelected[objIndex].projects.push(new mkProject({ id: QCS_ID_FOR_CLIENT_ONLY_UI_OPTION }));
    } else {
      const project = projectsSelectors.getProjectById(store.getState(), projectId) as Project;
      customersAndProjectsSelected[objIndex].projects.push(project);
    }
  }
  return customersAndProjectsSelected;
};

const getClientsAndProjectsFilterItemsCount = (filterObject: FilterObjectProps): number => {
  const selectedClientsAndProjectsInfo = filterObject.clientsAndProjects;
  if (!store || !selectedClientsAndProjectsInfo) return 0;

  const customersById = customersSelectors.getCustomers(store.getState());
  let count = 0;

  for (const selectedId of selectedClientsAndProjectsInfo) {
    if (customersById?.has(selectedId)) {
      count += 1;
    } else {
      const customerId = getCustomerIdOfSelectedProject(selectedId);
      if (customerId && !selectedClientsAndProjectsInfo.includes(customerId)) {
        count += 1;
      }
    }
  }
  return count;
};

export const countFilterItems = (filterObject: FilterObjectProps): number => {
  if (!filterObject) return 0;

  const count =
    ImmutableMap(filterObject)
      .filter((_, k) => k !== 'payees')
      .reduce((v: number, x: any) => v + (x?.size || 0), 0) || 0;
  return count + countDefinedItems(ImmutableMap(filterObject?.advanced?.toObject?.() ?? {}));
};

export const countFilterItemsForDisplay = (filterObject: FilterObjectProps): number => {
  if (!filterObject) return 0;

  let count =
    ImmutableMap(filterObject)
      .filter(
        (_, k) =>
          k !== 'payees' &&
          k !== 'businesses' &&
          k !== 'categories' &&
          k !== CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE &&
          k !== 'customersAndProjectsSelected',
      )
      .reduce((v: number, x: any) => v + (x?.size || 0), 0) || 0;

  count += getClientsAndProjectsFilterItemsCount(filterObject);

  count +=
    filterObject.categories?.reduce((accum: number, catId: string) => {
      const category = store && categoriesSelectors.getCategoryForId(store.getState(), catId);
      const hasParent = category && category.parentId && category.parentId !== '0';
      const parentIsSelected = hasParent && filterObject?.categories?.has(category.parentId);
      return accum + (parentIsSelected ? 0 : 1);
    }, 0) || 0;

  count += countDefinedItems(ImmutableMap(filterObject?.advanced?.toObject?.() ?? {}));
  return count;
};

export const advancedKeyToName: Record<string, string> = {
  isBillOrSubscription: 'Bills & Subscriptions',
  isExcludedFromF2S: 'Excluded in spending plan',
  isExcludedFromReports: 'Excluded in reports',
  isReviewed: 'Reviewed',
};

const getNameForClientsAndProjects = (searchItemId: string): string => {
  let customerName = '';
  if (!store) return customerName;

  const customersById = customersSelectors.getCustomers(store.getState());

  if (customersById?.has(searchItemId)) {
    const customer = customersById.get(searchItemId);
    customerName = customer?.businessName || `${customer?.firstName || ''} ${customer?.lastName || ''}` || 'Unknown';
    return customerName;
  }

  if (searchItemId.includes(VALUE_DELIMITER_FOR_CLIENT_ONLY_UI_OPTION)) {
    const items = searchItemId.split(VALUE_DELIMITER_FOR_CLIENT_ONLY_UI_OPTION);
    const clientOnlyUIOptionCustomerId = items[0];
    if (customersById.has(clientOnlyUIOptionCustomerId)) {
      const customer = customersById.get(clientOnlyUIOptionCustomerId);
      customerName = customer?.businessName || `${customer?.firstName || ''} ${customer?.lastName || ''}` || 'Unknown';
      return `${customerName}: ${DISPLAY_NAME_FOR_CLIENT_ONLY_UI_OPTION}`;
    } else {
      return 'error';
    }
  }
  const projectsByCustomerId = projectsSelectors.getProjectsByCustomerId(store.getState());

  for (const [customerId, customer] of customersById) {
    const projects = projectsByCustomerId.get(customerId)?.allProjects;
    if (projects) {
      for (const project of projects) {
        if (project?.id === searchItemId) {
          customerName =
            customer?.businessName || `${customer?.firstName || ''} ${customer?.lastName || ''}` || 'Unknown';
          return `${customerName}: ${project.name}`;
        }
      }
    }
  }
  return 'error';
};

const getCategoryName = (
  categoriesById: ImmutableMap<string, any>,
  it: string,
  accountsById: ImmutableMap<string, any>,
): string => {
  switch (it) {
    case 'BALANCE_ADJUSTMENT_0':
      return 'Balance Adjustment';
    case 'BALANCE_ADJUSTMENT_1':
      return 'Opening Balance';
    case 'BALANCE_ADJUSTMENT_2':
      return 'Transfer';
    case 'BALANCE_ADJUSTMENT_3':
      return 'Credit Card Payment';
    case '0':
    case 'UNCATEGORIZED':
      return 'Uncategorized';
    case 'OTHER':
      return 'Other';
    case 'TRANSFER':
      return 'Transfer';
    case 'USAGE-PERSONAL':
      return 'Personal';
    case 'USAGE-BUSINESS':
      return 'Business';
    default:
      if (categoriesById?.has(it)) {
        return categoriesById.get(it)?.name || '';
      }
      if (accountsById?.has(it)) {
        return `Transfer: ${accountsById.get(it)?.name || ''}`;
      }
      return 'error';
  }
};

export const getNameFromKeyFactory =
  (
    categoriesById: ImmutableMap<string, any>,
    payeesByName: ImmutableMap<string, any>,
    tagsById: ImmutableMap<string, any>,
    accountsById: ImmutableMap<string, any>,
    chartOfAccounts: { id: string; name: string }[] | undefined,
  ) =>
  (itemKey: string, item: any): string => {
    switch (itemKey) {
      case 'categories':
        return (
          chartOfAccounts?.find((coa) => item === coa.id)?.name || getCategoryName(categoriesById, item, accountsById)
        );
      case CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE:
        return getNameForClientsAndProjects(item);
      case 'payees':
        return payeesByName.get(item?.toLowerCase()) || '';
      case 'tags':
        return tagsById.get(item)?.name || '';
      case 'accounts':
        return accountsById.get(item)?.name || '';
      case 'flags':
        return flagFieldOptions.find(({ value }) => value === item)?.label || '';
      case 'advanced':
        return `${!item.val ? 'Not' : ''} ${advancedKeyToName[item.key]}`;
      default:
        return 'error';
    }
  };
